import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * Load an auth-gated proxy image (`/api/assets/:id/image`) as an object URL.
 *
 * Fetches the proxied asset as a blob via {@link api.fetchAssetBlob}, turns it
 * into an object URL, and returns that URL (or null while loading / on failure).
 * The object URL is revoked and the in-flight request aborted on cleanup, so it
 * is safe to use directly as an `<img src>`. Best-effort: failures resolve to
 * null rather than throwing.
 *
 * @param proxyUrl Stored proxy URL or null. Pass null to clear.
 * @param tenantSlug The workspace the asset belongs to, when it is not the active one.
 */
export function useProxyImage(proxyUrl: string | null, tenantSlug?: string): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!proxyUrl) {
      setObjectUrl(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    const controller = new AbortController();

    void api
      .fetchAssetBlob(proxyUrl, controller.signal, tenantSlug)
      .then((blob) => {
        if (cancelled || !blob) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        // Best-effort: fall back to null (caller renders a placeholder).
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (created) URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [proxyUrl, tenantSlug]);

  return objectUrl;
}

/**
 * Resolve a post/asset `thumbnailUrl` into a ready `<img src>`.
 *
 * Thumbnails come in two flavours: an **external** `https://` URL (a public/SAS link) or an
 * **auth-gated proxy path** like `/api/assets/:id/image`. The proxy sits behind the bearer-token
 * `JwtAuthGuard`, so a plain `<img src="/api/assets/…">` gets a 401 (the browser can't attach the
 * token) → the caller shows a placeholder even though the post HAS an image. This hook fetches
 * proxy paths as an object URL (via {@link useProxyImage}) and returns external URLs as-is.
 * Returns null while a proxy image loads or on failure (caller renders the placeholder).
 */
export function useThumbnailSrc(thumbnailUrl: string | null): string | null {
  const isExternal = !!thumbnailUrl && /^https?:\/\//i.test(thumbnailUrl);
  // Only proxy paths need the authed blob fetch; external URLs load directly (and we avoid
  // sending our bearer token to a third-party host).
  const proxied = useProxyImage(isExternal ? null : thumbnailUrl);
  return isExternal ? thumbnailUrl : proxied;
}
